import {
  AddManyParams,
  WixPatternsContainer,
  CollectionState,
  FiltersMap,
} from '@wix/bex-core';
import { computed, makeObservable, observable } from 'mobx';
import {
  ToolbarCollectionState,
  ToolbarCollectionStateParamsInitParams,
} from './ToolbarCollectionState';
import { DragAndDropState } from '../components/DragAndDrop';
import { RectState } from './RectState';
import { GridSizePreset } from './gridSizePresets';

export interface GridStateParams<T, F extends FiltersMap> {
  readonly collection: CollectionState<T, F>;
  readonly container: WixPatternsContainer;
  readonly toolbar: ToolbarCollectionState<any, Partial<F>>;
  readonly sizePreset: GridSizePreset;
}

export interface GridStateInitParams
  extends ToolbarCollectionStateParamsInitParams {}

export class GridState<T, F extends FiltersMap> {
  readonly toolbar;
  readonly container;
  readonly collection;
  readonly _containerRectState;

  sizePreset;

  trailingItemsCount = 0;
  minItemWidth = 196;
  gap = 24;
  smallCardWidthThreshold = 200;
  preset: 'full' | 'title' | 'empty' = 'title';
  _draggable: 'none' | 'draggable' = 'none';
  _localLimit: number | null = null;
  _dnd?: DragAndDropState<T, F> | null = null;
  forceRenderIndexes: { rowIndex: number; columnIndex: number }[] | undefined =
    undefined;

  constructor(params: GridStateParams<T, F>) {
    this.container = params.container;
    this.collection = params.collection;
    this._containerRectState = new RectState({
      container: this.container,
    });
    this.toolbar = params.toolbar as ToolbarCollectionState<
      unknown,
      Partial<F>
    >;
    this.sizePreset = params.sizePreset;

    makeObservable(this, {
      _dnd: observable.ref,
      trailingItemsCount: observable.ref,
      forceRenderIndexes: observable.ref,
      preset: observable.ref,
      _draggable: observable.ref,
      draggable: computed,
      itemsToRenderCount: computed,
      itemsToRender: computed,
      rowCount: computed,
      isInteracting: computed,
      calcGridProps: computed,
      columnsCount: computed,
      _sizeProps: computed,
      itemKeyRecyclerOverride: computed,
      _localLimit: observable.ref,
      itemsContentWidth: computed,
      sizePreset: observable.ref,
    });
  }

  get imagePlacement() {
    return this.sizePreset.imagePlacement;
  }

  get aspectRatio() {
    return this.sizePreset.aspectRatio;
  }

  get footerHeights() {
    return this.sizePreset.footerHeights;
  }

  get itemsContentWidth() {
    return this._containerRectState.rect.width ?? 0;
  }

  get draggable() {
    if (this._dnd?.isDisabled) {
      return 'none' as const;
    }
    return this._draggable;
  }

  get window() {
    return this.container.window;
  }

  get itemsToRenderCount() {
    const { collection, trailingItemsCount, _localLimit } = this;
    const {
      result: { keyedItems },
    } = collection;

    const itemsCount =
      _localLimit != null
        ? Math.min(_localLimit, keyedItems.length)
        : keyedItems.length;

    return !collection.fetchedLastPage
      ? itemsCount - Math.floor(itemsCount % this.columnsCount)
      : itemsCount + trailingItemsCount;
  }

  get reportBi() {
    return this.toolbar.reportBi;
  }

  get columnsCount() {
    return this.calcGridProps.columnCount;
  }

  get containerRect() {
    return this.toolbar.containerRect;
  }

  get rowCount() {
    const { itemsToRenderCount, columnsCount } = this;

    return Math.ceil(itemsToRenderCount / columnsCount);
  }

  get isInteracting() {
    return this._dnd?.isActive || this._dnd?.isDropping;
  }

  get itemsToRender(): null[] {
    const { itemsToRenderCount } = this;
    return new Array(itemsToRenderCount).fill(null);
  }

  getItemRenderTypeAt(index: number) {
    const { collection, itemsToRenderCount } = this;
    const {
      result: { keyedItems },
    } = collection;

    if (index >= itemsToRenderCount) {
      return null;
    }

    if (index >= keyedItems.length) {
      return 'trailing';
    }

    return keyedItems[index];
  }

  get errorMonitor() {
    return this.container.errorMonitor;
  }

  get _sizeProps() {
    const {
      minItemWidth,
      aspectRatio,
      gap,
      itemsContentWidth: width,
      preset,
      draggable,
    } = this;

    const widthWithLastColumnGap = width + gap;
    const maxItemWidthWithGap = minItemWidth + gap;

    const columnCount = Math.max(
      Math.floor(widthWithLastColumnGap / maxItemWidthWithGap),
      1,
    );

    const widthWithoutGaps = width - (columnCount - 1) * gap;
    const columnWidth = widthWithoutGaps / columnCount;

    const cardFooterSize = 'small' as const;

    const cardFooterHeight =
      this.footerHeights[preset][draggable][cardFooterSize];
    const cardTopImageHeight = columnWidth * aspectRatio;
    const rowHeight = cardTopImageHeight + cardFooterHeight + 1;

    const columnWidthIncludingGap = widthWithLastColumnGap / columnCount;
    const rowHeightIncludingGap = rowHeight + gap;

    return {
      columnCount,
      columnWidth: () => columnWidth,
      columnWidthIncludingGap: () => columnWidthIncludingGap,
      rowHeight: () => rowHeight,
      rowHeightFixed: rowHeight,
      cardTopImageHeight,
      rowHeightIncludingGap: () => rowHeightIncludingGap,
      cardFooterHeight,
      cardFooterSize,
    };
  }

  get calcGridProps() {
    const {
      toolbar: { itemsContentHeight },
      _sizeProps: { rowHeightFixed },
    } = this;

    const overscanCount = Math.round(
      (itemsContentHeight / rowHeightFixed) * 1.5,
    );

    return {
      ...this._sizeProps,
      overscanCount,
    };
  }

  get getCollectionSnapshot() {
    return this.toolbar.getCollectionSnapshot;
  }

  onAddItemClick = (origin?: string) => {
    this.toolbar.toolbarBIReporter.onAddItemClick(origin);
  };

  onAddItemActionComplete = async <R>(
    items: T[],
    params: AddManyParams<T, R> = {},
  ) => {
    return this.toolbar.onAddItemActionComplete(
      items,
      params as AddManyParams<any, R>,
    );
  };

  get itemKeyRecyclerOverride() {
    const { collection } = this;
    const {
      result: { keyedItems },
    } = collection;
    return (index: number) => {
      return keyedItems[index]?.key ?? index;
    };
  }

  get dragAndDropCategories() {
    return this.toolbar.dragAndDropCategories;
  }

  get listAttributes() {
    return {
      ...this._dnd?.nullAnnouncements.listAttributes?.(),
      role: 'list',
    };
  }
}
